import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  isAuthenticated,
  getInviteCode,
  getStoredEmail,
  setInviteCode,
  setSessionToken,
  setStoredEmail,
} from "../lib/api";

function isValidEmail(email: string): boolean {
  const atIdx = email.indexOf("@");
  if (atIdx < 1) return false;
  const afterAt = email.slice(atIdx + 1);
  return afterAt.includes(".") && afterAt.indexOf(".") < afterAt.length - 1;
}

const LOADING_MESSAGES = [
  "Researching your market...",
  "Analyzing competitors...",
  "Finding industry benchmarks...",
  "Preparing your interrogation...",
];

export default function Landing() {
  const navigate = useNavigate();
  const [backendReady, setBackendReady] = useState(false);
  const [checkingStored, setCheckingStored] = useState(true);

  // Section 1: New user state
  const [newEmail, setNewEmail] = useState("");
  const [idea, setIdea] = useState("");
  const [newLoading, setNewLoading] = useState(false);
  const [newError, setNewError] = useState("");
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [showSlowWarning, setShowSlowWarning] = useState(false);
  const loadingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Section 2: Returning user state
  const [returnEmail, setReturnEmail] = useState("");
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Section 3: Invite code state
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeEmail, setCodeEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState("");

  // Prefill idea from ?idea= URL parameter
  useEffect(() => {
    const prefill = new URLSearchParams(window.location.search).get("idea");
    if (prefill) setIdea(prefill);
  }, []);

  // Wake backend + check stored credentials
  useEffect(() => {
    const storedEmail = getStoredEmail();
    if (storedEmail) {
      setNewEmail(storedEmail);
      setReturnEmail(storedEmail);
      setCodeEmail(storedEmail);
    }

    api
      .healthCheck()
      .then((ok) => {
        setBackendReady(ok);
        return ok;
      })
      .catch(() => {
        setTimeout(() => {
          api
            .healthCheck()
            .then(() => setBackendReady(true))
            .catch(() => setBackendReady(false));
        }, 3000);
        return false;
      })
      .then(() => {
        if (isAuthenticated()) {
          const stored = getInviteCode();
          const storedMail = getStoredEmail();
          if (stored) {
            api
              .verifyCode(stored, storedMail || "")
              .then((res) => {
                if (res.valid) {
                  navigate("/dashboard", { replace: true });
                } else {
                  setCheckingStored(false);
                }
              })
              .catch(() => setCheckingStored(false));
          } else {
            api
              .listProjects()
              .then(() => navigate("/dashboard", { replace: true }))
              .catch(() => setCheckingStored(false));
          }
        } else {
          setCheckingStored(false);
        }
      });
  }, [navigate]);

  // Cleanup loading timers
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) clearInterval(loadingTimerRef.current);
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    };
  }, []);

  // --- Section 1: Start new interrogation ---

  async function handleNewSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = newEmail.trim();
    const trimmedIdea = idea.trim();
    if (!isValidEmail(trimmedEmail) || !trimmedIdea) return;

    setNewError("");
    setNewLoading(true);
    setLoadingMsgIndex(0);
    setShowSlowWarning(false);

    // Rotate loading messages every 5 seconds
    loadingTimerRef.current = setInterval(() => {
      setLoadingMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 5000);

    // Show slow warning after 45 seconds
    slowTimerRef.current = setTimeout(() => {
      setShowSlowWarning(true);
    }, 45000);

    try {
      setStoredEmail(trimmedEmail);
      const res = await api.createProject(trimmedIdea, undefined, "English", trimmedEmail);
      if (res.session_token) {
        setSessionToken(res.session_token);
      }
      navigate(`/chat?project=${res.project_id}`);
    } catch (err) {
      if (err instanceof ApiError && err.detail && typeof err.detail === "object") {
        const detail = err.detail as Record<string, string>;
        if (detail.error === "active_project_exists") {
          setNewError(
            `You already have an active project: "${detail.project_title || "Untitled"}". Go to your dashboard to continue or archive it.`,
          );
          return;
        }
        if (detail.error === "free_limit_reached") {
          setNewError(
            "You've used your 3 free interrogations. Unlock the full assessment on an existing project to keep going.",
          );
          return;
        }
      }
      const msg =
        err instanceof Error ? err.message : "Could not start interrogation. Please try again.";
      setNewError(msg);
    } finally {
      setNewLoading(false);
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }
      setShowSlowWarning(false);
    }
  }

  // --- Section 2: Returning user magic link ---

  async function handleReturnSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = returnEmail.trim();
    if (!isValidEmail(trimmedEmail)) return;

    setReturnError("");
    setReturnLoading(true);
    try {
      const res = await api.requestMagicLink(trimmedEmail);
      if (res.sent) {
        setStoredEmail(trimmedEmail);
        setMagicLinkSent(true);
      } else {
        setReturnError("Could not send login link. Please try again.");
      }
    } catch {
      setReturnError("Could not send login link. Please try again.");
    } finally {
      setReturnLoading(false);
    }
  }

  // --- Section 3: Invite code ---

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedCode = code.trim();
    const trimmedEmail = codeEmail.trim();
    if (!trimmedCode || !isValidEmail(trimmedEmail)) return;

    setCodeError("");
    setCodeLoading(true);
    try {
      const res = await api.verifyCode(trimmedCode, trimmedEmail);
      if (res.valid) {
        setInviteCode(trimmedCode);
        setStoredEmail(trimmedEmail);
        navigate("/dashboard");
      } else {
        setCodeError("Invalid or expired invite code");
      }
    } catch {
      setCodeError("Could not verify code. Please try again.");
    } finally {
      setCodeLoading(false);
    }
  }

  // --- Render ---

  if (checkingStored) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  const anyLoading = newLoading || returnLoading || codeLoading;

  return (
    <div className="min-h-screen bg-slate-900 px-4 py-12 sm:py-16">
      <div className="w-full max-w-lg mx-auto">
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
            Architect
          </h1>
          <p className="mt-3 text-lg text-slate-400">
            From idea to implementation plan
            <br />
            in one conversation
          </p>
        </div>

        {/* ============================================================ */}
        {/* SECTION 1: Start a new interrogation (PRIMARY)               */}
        {/* ============================================================ */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-4">
            Start a new interrogation
          </h2>

          <form onSubmit={handleNewSubmit} className="space-y-3">
            <div className="text-left">
              <label
                htmlFor="new-email"
                className="block text-xs text-slate-500 mb-1 ml-1"
              >
                Your email (we'll send your assessment results here)
              </label>
              <input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={newLoading}
                autoFocus
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-base placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 disabled:opacity-50"
              />
            </div>

            <div className="text-left">
              <label
                htmlFor="idea-input"
                className="block text-xs text-slate-500 mb-1 ml-1"
              >
                Your idea
              </label>
              <textarea
                id="idea-input"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                placeholder="Describe your startup idea in 1-2 sentences..."
                rows={3}
                disabled={newLoading}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-base placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 resize-y disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={
                anyLoading ||
                !backendReady ||
                !isValidEmail(newEmail.trim()) ||
                idea.trim().length === 0
              }
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-xl px-4 py-3 text-base transition-colors flex items-center justify-center gap-2"
            >
              {newLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Starting...
                </>
              ) : (
                "Start Interrogation"
              )}
            </button>

            {/* Loading status messages */}
            {newLoading && (
              <p className="text-sm text-blue-400 text-center animate-pulse">
                {showSlowWarning
                  ? "This is taking longer than expected. Please wait..."
                  : LOADING_MESSAGES[loadingMsgIndex]}
              </p>
            )}

            {newError && (
              <div className="text-center">
                <p className="text-sm text-red-400">{newError}</p>
                {(newError.includes("active project") || newError.includes("3 free interrogations")) && (
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="mt-2 text-sm text-blue-400 hover:text-blue-300 underline transition-colors"
                  >
                    View my projects
                  </button>
                )}
              </div>
            )}
          </form>

          <p className="mt-3 text-xs text-slate-600">
            We'll send your assessment results and one follow-up tip to this
            email. No spam, unsubscribe anytime.
          </p>
          <p className="mt-2 text-xs text-slate-600">
            Your idea stays yours. We don't claim ownership, share, or store
            your business concepts beyond your session.
          </p>
        </section>

        {/* ============================================================ */}
        {/* SECTION 2: Returning user — magic link (SECONDARY)           */}
        {/* ============================================================ */}
        <section className="mb-6 border-t border-slate-800 pt-8">
          <h2 className="text-base font-medium text-slate-300 mb-3">
            Already used Architect?
          </h2>

          {magicLinkSent ? (
            <div className="space-y-3">
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <p className="text-white text-sm font-medium mb-1">
                  Check your inbox
                </p>
                <p className="text-slate-400 text-xs">
                  We sent a login link to{" "}
                  <strong className="text-white">{returnEmail}</strong>. It
                  expires in 15 minutes.
                </p>
              </div>
              <button
                onClick={() => setMagicLinkSent(false)}
                className="text-xs text-slate-500 hover:text-white transition-colors"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleReturnSubmit}
              className="flex gap-2 items-start"
            >
              <input
                type="email"
                value={returnEmail}
                onChange={(e) => setReturnEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={returnLoading}
                className="flex-1 bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={
                  anyLoading ||
                  !backendReady ||
                  !isValidEmail(returnEmail.trim())
                }
                className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors whitespace-nowrap"
              >
                {returnLoading ? "Sending..." : "Send me a login link"}
              </button>
            </form>
          )}

          {returnError && (
            <p className="text-xs text-red-400 mt-2">{returnError}</p>
          )}
        </section>

        {/* ============================================================ */}
        {/* SECTION 3: Invite code (MINIMAL — collapsed by default)      */}
        {/* ============================================================ */}
        <section className="border-t border-slate-800 pt-4">
          {!showCodeInput ? (
            <button
              onClick={() => setShowCodeInput(true)}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              Have an invite code?
            </button>
          ) : (
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-slate-400">
                Invite Code
              </h2>
              <form onSubmit={handleCodeSubmit} className="space-y-2">
                <input
                  type="email"
                  value={codeEmail}
                  onChange={(e) => setCodeEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={codeLoading}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 disabled:opacity-50"
                />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="XXXX-XXXX"
                  disabled={codeLoading}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-center text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 disabled:opacity-50"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={
                      anyLoading ||
                      !backendReady ||
                      code.trim().length === 0 ||
                      !isValidEmail(codeEmail.trim())
                    }
                    className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
                  >
                    {codeLoading ? "Verifying..." : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCodeInput(false)}
                    className="text-xs text-slate-500 hover:text-white transition-colors px-2"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              {codeError && (
                <p className="text-xs text-red-400">{codeError}</p>
              )}
            </div>
          )}
        </section>

        {/* Backend status */}
        {!backendReady && (
          <p className="text-slate-500 text-xs text-center mt-4">
            Connecting to server...
          </p>
        )}
      </div>
    </div>
  );
}
